import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Row, View } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';

import {
  IMPACT_DIRECTION,
  type ImpactDirection,
  type ImpactShare,
} from '../logic/riskMetricGroup';
import { RISK_ENGINE_TEST_IDS } from '../testIds';

/** Bar height in px — tall enough to read as a magnitude, unlike a hairline progress track. */
const IMPACT_BAR_HEIGHT_PX = 12;
/** Keeps the track visibly wide even when the column or label squeezes the row. */
const IMPACT_BAR_MIN_WIDTH_PX = 64;

/**
 * The bar's track: transparent (so the green/red fill isn't washed out by a grey background) with a
 * thin light-grey outline (Stencil's standard `color.border.primary` hairline) that still shows the
 * bar's full 0–100% extent. `border-box` keeps the
 * outline inside the 12px height; `overflow: hidden` + the radius clip the fill to the rounded
 * track. Module-level so emotion sees one stable object.
 */
const IMPACT_BAR_TRACK_STYLE = {
  flex: 1,
  minWidth: IMPACT_BAR_MIN_WIDTH_PX,
  height: IMPACT_BAR_HEIGHT_PX,
  boxSizing: 'border-box',
  border: `${token('dimensions.border.width.100')} solid ${token('color.border.primary')}`,
  borderRadius: IMPACT_BAR_HEIGHT_PX / 2,
  overflow: 'hidden',
} as const;

/**
 * Two colours only, reusing the same status tokens as the risk status indicator: hurting = status
 * red, everything else = status green. A zero-magnitude (neutral) driver renders no visible fill,
 * so its colour never actually shows.
 */
export function impactFillToken(direction: ImpactDirection): string {
  return direction === IMPACT_DIRECTION.hurting
    ? 'color.status.critical-icon'
    : 'color.status.success-icon';
}

/** Clamps the share to a 0–100 CSS width so an out-of-range value can't overflow the track. */
export function impactBarWidth(sharePct: number): string {
  return `${Math.max(0, Math.min(100, sharePct))}%`;
}

interface ImpactBarProps {
  readonly share: ImpactShare;
  /** The formatted figure shown beside the bar (e.g. "39%"). */
  readonly label: string;
}

/**
 * A solid rectangle whose width is the driver's impact share and whose colour is its direction —
 * status red for hurting, status green for helping — with the exact figure beside it. The track is
 * transparent (no grey background) so the fill stays legible, with a thin grey outline marking the
 * bar's full extent.
 */
export function ImpactBar({ share, label }: ImpactBarProps): JSX.Element {
  return (
    <Row alignItems="center" gridGap="dimensions.spacing.200" minWidth={0} width="100%">
      <View css={IMPACT_BAR_TRACK_STYLE}>
        <View
          backgroundColor={impactFillToken(share.direction)}
          dataTestId={RISK_ENGINE_TEST_IDS.impactBarFill}
          css={{ width: impactBarWidth(share.sharePct), height: '100%' }}
        />
      </View>
      <Text fontSize="T100">{label}</Text>
    </Row>
  );
}
